import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { Flag } from "@/components/flag";
import { Tabs, type Tab } from "@/components/tabs";
import {
  availableCollections,
  type CollectionItem,
  type DataSourceItem,
  type DataSourceRegion,
} from "./catalog";

const REGION_TABS: ReadonlyArray<"Collections" | DataSourceRegion> = [
  "Collections",
  "International",
  "Europe",
  "Americas",
  "Africa",
  "Asia Oceania",
];

const REGION_TAB_ITEMS: Tab[] = REGION_TABS.map((id) => ({ id, label: id }));

/** Twemoji renders proper flags for ISO-3166 alpha-2 codes only. */
function isoForCode(code: string): string | null {
  if (code === "UK") return "GB";
  if (/^[A-Z]{2}$/.test(code)) return code;
  return null;
}

/**
 * Leading icon for a data-source chip. Globe for International region items,
 * country flag otherwise. Falls back to globe when the code isn't ISO-shaped.
 */
export function DataSourceChipIcon({ item, size = 16 }: { item: DataSourceItem; size?: number }) {
  // Material Symbols supports a discrete optical-size axis; pick the closest.
  const iconSize: 20 | 24 = size >= 22 ? 24 : 20;
  if (item.region === "International") {
    return <Icon name="public" size={iconSize} className="shrink-0" />;
  }
  const iso = isoForCode(item.code);
  if (iso) return <Flag iso={iso} size={size} />;
  return <Icon name="public" size={iconSize} className="shrink-0" />;
}

/** Icon for a collection chip. Sanctions Lists → favicon; others → simple symbols. */
export function CollectionChipIcon({ id, size = 16 }: { id: string; size?: number }) {
  const iconSize: 20 | 24 = size >= 22 ? 24 : 20;
  if (id === "coll:sanctions-lists") {
    return (
      <img
        src="/brand/logo-collapsed.svg"
        alt=""
        style={{ width: size, height: size }}
        className="shrink-0"
        draggable={false}
      />
    );
  }
  const icon =
    id === "coll:pep-lists"
      ? "person"
      : id === "coll:crime-lists"
        ? "gavel"
        : id === "coll:high-risk-jurisdictions"
          ? "warning"
          : id === "coll:adverse-media"
            ? "newspaper"
            : "folder";
  return <Icon name={icon} size={iconSize} className="shrink-0" />;
}

type Props = {
  anchor?: HTMLElement | null;
  items?: DataSourceItem[];
  initialSelected: string[];
  defaultSelected?: string[];
  onApply: (ids: string[]) => void;
  onClose: () => void;
  /** Company entitlements — hide the PEP Lists / Adverse Media collections when not allowed. Default true (unrestricted). */
  pepAllowed?: boolean;
  adverseMediaEnabled?: boolean;
};

export function DataSourcePopup({
  items = [],
  initialSelected,
  defaultSelected = [],
  onApply,
  pepAllowed = true,
  adverseMediaEnabled = true,
}: Props) {
  const collections = useMemo(
    () => availableCollections(pepAllowed, adverseMediaEnabled),
    [pepAllowed, adverseMediaEnabled],
  );
  const [region, setRegion] = useState<(typeof REGION_TABS)[number]>("Collections");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialSelected));

  // Closing the popup commits the current selection — there is no
  // explicit Apply action.
  function commit() {
    onApply([...selected]);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") commit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, onApply]);

  const isCollectionsTab = region === "Collections";
  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  // Figma reads "Watchlist" while browsing the grouped Collections tab, and
  // "Data Source" once you're looking at individual sources — a region tab or a
  // search (frames 15861-48840 "Dialog - Watchlist" / 15478-25486 "Dialog - Data source").
  const title = isCollectionsTab && !isSearching ? "Watchlist" : "Data Source";

  const filteredSources = useMemo(() => {
    // Every data-source list (a region tab or a search's "Data sources"
    // section) reads alphabetically by country code, then by name as a
    // tiebreaker — only the Collections list below keeps its own curated
    // order.
    const matches = isSearching
      ? items.filter(
          (i) =>
            i.name.toLowerCase().includes(trimmedQuery) ||
            i.code.toLowerCase().includes(trimmedQuery) ||
            i.region.toLowerCase().includes(trimmedQuery),
        )
      : isCollectionsTab
        ? []
        : items.filter((i) => i.region === region);
    return [...matches].sort(
      (a, b) => a.code.localeCompare(b.code) || a.name.localeCompare(b.name),
    );
  }, [items, region, trimmedQuery, isCollectionsTab, isSearching]);

  const filteredCollections = useMemo(() => {
    if (isSearching) {
      return collections.filter((c) => c.label.toLowerCase().includes(trimmedQuery));
    }
    if (!isCollectionsTab) return [];
    return collections;
  }, [collections, trimmedQuery, isCollectionsTab, isSearching]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* z-[1390]/[1400]: above the MUI Dialog (zIndex.modal=1300) so the
          watchlist picker shows in front of the add-monitoring modal. */}
      <div aria-hidden className="fixed inset-0 z-[1390] bg-grey-900-24" onClick={commit} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ top: 200 }}
        className="fixed left-1/2 z-[1400] flex w-[594px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-232px)] -translate-x-1/2 flex-col gap-3 rounded-[4px] border border-grey-200 bg-white p-4 shadow-soft"
      >
        <div className="flex shrink-0 items-center gap-3">
          <p className="flex-1 text-body font-semibold text-black">{title}</p>
          <button
            type="button"
            onClick={() => setSelected(new Set(defaultSelected))}
            className="inline-flex items-center gap-1.5 rounded-[4px] text-body font-medium text-black transition-colors hover:text-grey-900"
          >
            <Icon name="refresh" size={20} />
            Reset
          </button>
          <button
            type="button"
            onClick={commit}
            className="inline-flex items-center rounded-[4px] text-body font-medium text-green-700 transition-colors hover:text-green-600"
          >
            Apply ({selected.size})
          </button>
        </div>

        {/* border-transparent base + focus-within:border-grey-500 gives the
            input a visible keyboard-focus ring with no layout shift (the input
            itself is outline-none). */}
        <div className="flex shrink-0 items-center gap-1.5 rounded-[4px] border border-transparent bg-grey-100 p-2 focus-within:border-grey-500">
          <Icon name="search" size={20} className="text-grey-500" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            aria-label="Search watchlists"
            className="min-w-0 flex-1 border-0 bg-transparent text-small text-black outline-none placeholder:text-grey-500"
          />
        </div>

        {!isSearching && (
          <div className="shrink-0">
            <Tabs
              tabs={REGION_TAB_ITEMS}
              value={region}
              onChange={(id) => setRegion(id as (typeof REGION_TABS)[number])}
            />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {isSearching ? (
            filteredCollections.length === 0 && filteredSources.length === 0 ? (
              <p className="px-2 py-4 text-small text-grey-500">
                No matches for &ldquo;{query}&rdquo;.
              </p>
            ) : (
              <>
                {filteredCollections.length > 0 && (
                  <>
                    <SectionHeader label="Collections" />
                    {filteredCollections.map((c) => (
                      <CollectionCheckItem
                        key={c.id}
                        item={c}
                        checked={selected.has(c.id)}
                        onToggle={() => toggle(c.id)}
                      />
                    ))}
                  </>
                )}
                {filteredSources.length > 0 && (
                  <>
                    <SectionHeader label="Data sources" />
                    {filteredSources.map((item) => (
                      <CheckItem
                        key={item.id}
                        item={item}
                        checked={selected.has(item.id)}
                        onToggle={() => toggle(item.id)}
                        showRegion
                      />
                    ))}
                  </>
                )}
              </>
            )
          ) : isCollectionsTab ? (
            filteredCollections.map((c) => (
              <CollectionCheckItem
                key={c.id}
                item={c}
                checked={selected.has(c.id)}
                onToggle={() => toggle(c.id)}
              />
            ))
          ) : (
            filteredSources.map((item) => (
              <CheckItem
                key={item.id}
                item={item}
                checked={selected.has(item.id)}
                onToggle={() => toggle(item.id)}
              />
            ))
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <p className="px-2 pt-3 pb-1 text-tiny font-semibold uppercase tracking-wide text-grey-500 first:pt-1">
      {label}
    </p>
  );
}

function CheckItem({
  item,
  checked,
  onToggle,
  showRegion = false,
}: {
  item: DataSourceItem;
  checked: boolean;
  onToggle: () => void;
  showRegion?: boolean;
}) {
  return (
    <label
      className={[
        "flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-2 text-small transition-colors",
        checked ? "bg-green-500-12" : "hover:bg-grey-50",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "flex size-5 shrink-0 items-center justify-center rounded-[2px] border transition-colors",
          checked
            ? "border-green-600 bg-green-500 text-white"
            : "border-grey-300 bg-white text-transparent",
        ].join(" ")}
      >
        <Icon name="check" size={20} />
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={onToggle} />
      <span className="text-grey-500">{item.code}</span>
      <span className="text-grey-300">/</span>
      <span className="min-w-0 flex-1 truncate text-black">{item.name}</span>
      {showRegion && <span className="shrink-0 text-tiny text-grey-500">{item.region}</span>}
    </label>
  );
}

function CollectionCheckItem({
  item,
  checked,
  onToggle,
}: {
  item: CollectionItem;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={[
        "flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-2 text-small transition-colors",
        checked ? "bg-green-500-12" : "hover:bg-grey-50",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "flex size-5 shrink-0 items-center justify-center rounded-[2px] border transition-colors",
          checked
            ? "border-green-600 bg-green-500 text-white"
            : "border-grey-300 bg-white text-transparent",
        ].join(" ")}
      >
        <Icon name="check" size={20} />
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={onToggle} />
      <span className="min-w-0 flex-1 truncate text-black">
        {item.label}
        {item.id === "coll:sanctions-lists" && (
          // Sanctions Lists is searched by default when no watchlist is picked
          // (Figma 15861-95103 — muted "(default)" suffix).
          <span className="text-grey-600"> (default)</span>
        )}
      </span>
    </label>
  );
}
